import { useCallback, useState } from 'react';
import type { GoodsReceiptNote } from '../../../../lib/api/types/grn-types';
import { isBulkAcknowledgeEligible, isBulkAppealEligible } from './bulkSelection';

/** The Deliveries grid's multi-select state and what each bulk action covers. */
export interface DeliveryBulkSelection {
  /** Ids ticked on the current page. */
  selectedIds: Set<number>;
  /** Whether bulk Acknowledge is offered to this viewer (customer side only). */
  canAcknowledge: boolean;
  /** Whether a row gets a checkbox — some bulk action applies to it. */
  isSelectable: (grn: GoodsReceiptNote) => boolean;
  /** Selectable rows on the current page (the select-all target). */
  eligibleGrns: GoodsReceiptNote[];
  allEligibleSelected: boolean;
  /** Selected rows "Acknowledge Selected" applies to. */
  acknowledgeGrns: GoodsReceiptNote[];
  /** Selected rows "Appeal Selected" applies to. */
  appealGrns: GoodsReceiptNote[];
  toggle: (grn: GoodsReceiptNote) => void;
  toggleAll: () => void;
  clear: () => void;
}

/**
 * Multi-select for the Deliveries grid's bulk bar (CR-7420 appeal, CR-8214
 * acknowledge). Extracted from DeliveryTab, which is over the 500-line limit.
 *
 * A row is selectable when either bulk action applies to it; each button then
 * acts on its own subset of the selection. Acknowledge is customer-only — on
 * the supplier view the selection is exactly the CR-7420 appeal selection.
 * The caller clears the selection whenever the list is re-queried.
 *
 * @example
 * const bulk = useDeliveryBulkSelection(grns, bulkModeActive, isSupplierView);
 * <input type="checkbox" checked={bulk.selectedIds.has(grn.id)} onChange={() => bulk.toggle(grn)} />
 *
 * @param grns - the rows on the current page
 * @param bulkModeActive - the bulk controls are on for this viewer (`isBulkAppealModeActive`)
 * @param isSupplierView - the viewer is acting as a supplier
 * @returns the selection, the per-action subsets and the toggles
 */
export function useDeliveryBulkSelection(
  grns: GoodsReceiptNote[],
  bulkModeActive: boolean,
  isSupplierView: boolean,
): DeliveryBulkSelection {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const canAcknowledge = bulkModeActive && !isSupplierView;

  const isSelectable = useCallback((grn: GoodsReceiptNote): boolean =>
    bulkModeActive && (isBulkAppealEligible(grn) || (canAcknowledge && isBulkAcknowledgeEligible(grn))),
  [bulkModeActive, canAcknowledge]);

  const eligibleGrns = bulkModeActive ? grns.filter(isSelectable) : [];
  const allEligibleSelected = eligibleGrns.length > 0 && eligibleGrns.every((g) => selectedIds.has(g.id));
  const selectedGrns = grns.filter((g) => selectedIds.has(g.id));

  const toggle = useCallback((grn: GoodsReceiptNote) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(grn.id)) next.delete(grn.id);
      else next.add(grn.id);
      return next;
    });
  }, []);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (eligibleGrns.every((g) => prev.has(g.id))) eligibleGrns.forEach((g) => next.delete(g.id));
      else eligibleGrns.forEach((g) => next.add(g.id));
      return next;
    });
  };

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return {
    selectedIds,
    canAcknowledge,
    isSelectable,
    eligibleGrns,
    allEligibleSelected,
    acknowledgeGrns: canAcknowledge ? selectedGrns.filter(isBulkAcknowledgeEligible) : [],
    appealGrns: selectedGrns.filter(isBulkAppealEligible),
    toggle,
    toggleAll,
    clear,
  };
}
